import {
  changeCadFilePublicAccessLevel,
  changeCommentsEnabled,
} from '@api/cadFile.js';
import { lockFolderAccess } from '@api/projects.js';
import Popover from '@components/Common/Popover.js';
import toast from '@components/Common/Toast.js';
import { Toggle } from '@components/Common/Toggle.js';
import RolesModal from '@components/Modals/ShareModal/RolesModal.js';
import YesNoPromptModal from '@components/Modals/YesNoPromptModal.js';
import {
  CircleInfoMessageIcon,
  DropdownIcon,
  GlobeIcon,
} from '@icons/index.js';
import { UIButton, UIDiv, UIText } from '@ui';

export default class AccessControls {
  constructor(options = {}) {
    this.accessControl = options.accessControl || false;
    this.isPublic = options.isPublic || false;
    this.commentsEnabled = options.commentsEnabled || false;
    this.resourceName = options.resourceName || '';
    this.shortSharingId = options.shortSharingId || '';
    this.currentAccessLevel = options.currentAccessLevel || 'Restricted';
    this.resourceSharingId = options.resourceSharingId || '';
    this.onStateChange = options.onStateChange || (() => {});
    this.hasOrg = options.hasOrg || false;
    this.isProject = options.isProject || false;
    this.resourceId = options.resourceId || '';
  }

  render() {
    const container = new UIDiv();
    container.setClass('mb-6');

    // General access title
    const generalAccessTitle = new UIText(
      i18next.t('menubar.share.general_access'),
    );
    generalAccessTitle.setClass('font-medium mb-2 block');

    // Access level control
    const accessLevelControl = this.createAccessLevelControl();

    // Toggles section
    const togglesSection = this.createTogglesSection();

    if (this.isProject) {
      if (this.accessControl.hasPermission('update')) {
        container.add(togglesSection);
      }
    } else {
      if (this.accessControl.hasPermission('update')) {
        container.add(generalAccessTitle, accessLevelControl, togglesSection);
      } else {
        container.add(generalAccessTitle, accessLevelControl);
      }
    }
    return container;
  }

  createAccessLevelControl() {
    const accessLevelControl = new UIDiv();
    accessLevelControl.setClass(
      'bg-white h-[60px] flex justify-between items-center py-6 px-4 rounded-xl mb-4',
    );

    const resourceNameText = new UIText(this.resourceName);
    resourceNameText.setClass(
      'text-gray-700 truncate max-w-full text-center mb-2 sm:mb-0',
    );

    const accessLevelContainer = new UIDiv();
    accessLevelContainer.setClass('flex items-center gap-2');

    const infoIconButton = new UIButton();
    infoIconButton.setClass('ml-2');
    const infoIcon = new CircleInfoMessageIcon({ color: 'none', size: 16 });
    infoIconButton.add(infoIcon);
    infoIconButton.onClick(async () => {
      const rolesModal = new RolesModal();
      await rolesModal.show();
    });

    const accessLevelButton = new UIButton();
    accessLevelButton.setClass(
      'flex items-center gap-2 bg-gray-100 hover:bg-gray-200 py-1 px-3 rounded transition-all duration-300 w-full sm:w-auto justify-between sm:justify-start',
    );
    accessLevelButton.setDisabled(!this.accessControl.hasPermission('update'));

    const currentAccessLevel = new UIText(this.getAccessLevelText());
    currentAccessLevel.setClass('general-access-level-text');
    currentAccessLevel.setId('current-access-level');

    const dropdownArrow = new DropdownIcon({
      color: 'currentColor',
    });

    accessLevelButton.add(currentAccessLevel);
    if (this.accessControl.hasPermission('update')) {
      accessLevelButton.add(dropdownArrow);
    }

    if (this.accessControl.hasPermission('update')) {
      const popoverContent = new UIDiv();

      const updatePopoverContent = () => {
        popoverContent.dom.innerHTML = '';
        const levels = [];
        if (this.accessControl.hasPermission('invite_admin')) {
          levels.push('Edit');
        }
        if (this.accessControl.hasPermission('invite_manager')) {
          levels.push('Manager');
        }
        if (this.accessControl.hasPermission('invite_member')) {
          levels.push('View');
        }

        levels.forEach(level => {
          const option = new UIButton();
          option.setClass(
            'w-full rounded-md text-left px-4 py-2 hover:bg-gray-100 flex items-center justify-between',
          );

          const optionText = new UIText(
            i18next.t(`menubar.share.option_${level.toLowerCase()}`),
          );
          const checkmark = new UIDiv();

          if (this.currentAccessLevel === level) {
            checkmark.setClass('w-4 h-4 text-[var(--o-hive-primary-100)]');
            checkmark.setTextContent('✓');
          }

          option.add(optionText, checkmark);
          option.onClick(async () => {
            await this.changeAccessLevel(level);
            currentAccessLevel.setTextContent(this.getAccessLevelText());
            updatePopoverContent();
            Popover.hide();
          });

          popoverContent.add(option);
        });
      };

      updatePopoverContent();
      accessLevelButton.onClick(e => {
        e.stopPropagation();
        Popover.show({
          trigger: accessLevelButton,
          content: popoverContent,
          placement: 'bottom-start',
          hideOnSelect: false,
        });
      });

      accessLevelContainer.add(infoIconButton, accessLevelButton);

      accessLevelControl.add(resourceNameText, accessLevelContainer);
    } else {
      accessLevelControl.add(resourceNameText, accessLevelButton);
    }

    return accessLevelControl;
  }

  createTogglesSection() {
    const togglesSection = new UIDiv();

    const commentsToggle = new UIDiv();
    commentsToggle.setClass('flex justify-between items-center mb-4');

    const commentsLabel = new UIText(
      i18next.t('menubar.share.change_comment_mode'),
    );
    commentsLabel.setClass('font-medium');

    const commentsSwitch = new Toggle({
      initialState: this.commentsEnabled,
      onChange: async state => {
        await this.toggleCommentsEnabled(state);
      },
    });

    commentsToggle.add(commentsLabel, commentsSwitch);

    const publicToggle = new UIDiv();
    publicToggle.setClass('flex justify-between items-center mb-4');
    const publicLabelContainer = new UIDiv();
    publicLabelContainer.setClass('flex flex-col gap-1');

    const labelIconContainer = new UIDiv();
    labelIconContainer.setClass('flex flex-row gap-2 items-center');

    const publicLabel = new UIText(i18next.t('menubar.share.public_private'));
    publicLabel.setClass('font-medium');
    const publicIcon = new GlobeIcon({ color: 'white', size: 15 });

    const publicStatus = new UIText(this.isPublic ? 'Enabled' : 'Disabled');
    publicStatus.setClass('text-gray-500 text-sm ml-6');
    publicStatus.setId('public-access-status');

    labelIconContainer.add(publicIcon, publicLabel);
    publicLabelContainer.add(labelIconContainer, publicStatus);

    const publicSwitch = new Toggle({
      initialState: this.isPublic,
      onChange: async state => {
        const success = await this.togglePublicPrivate(state);
        if (success) {
          publicStatus.setTextContent(state ? 'Enabled' : 'Disabled');
        } else {
          publicSwitch.setState(!state);
        }
      },
    });

    publicToggle.add(publicLabelContainer, publicSwitch);

    if (!this.isProject) {
      togglesSection.add(commentsToggle, publicToggle);
    } else if (this.isProject) {
      togglesSection.add(publicToggle);
    }

    return togglesSection;
  }

  async changeAccessLevel(level, togglePublic = false) {
    try {
      if (this.isProject) {
        await lockFolderAccess(this.resourceId);
      } else {
        const { response } = await changeCadFilePublicAccessLevel(
          this.resourceSharingId,
          level,
        );
      }

      this.currentAccessLevel = level;
      this.isPublic = level !== 'Restricted';
      const translatedLevel = togglePublic
        ? i18next.t(`menubar.share.${this.isPublic ? 'public' : 'private'}`)
        : i18next.t(`menubar.share.option_${level.toLowerCase()}`);

      toast.success(
        i18next.t('menubar.share.toast.access_level_changed', {
          level: translatedLevel,
        }),
      );

      this.updateUI();
      const dropdown = document.getElementById('access-level-dropdown');
      if (dropdown) {
        dropdown.classList.add('hidden');
      }

      // Notify parent component of state changes
      this.onStateChange({
        isPublic: this.isPublic,
        currentAccessLevel: this.currentAccessLevel,
        cadfileAccessLevel: {
          level: this.currentAccessLevel,
        },
        accessLevelText: this.getAccessLevelText(),
      });
    } catch (error) {
      console.error('Failed to change access level:', error);
      toast.error(i18next.t('menubar.share.toast.access_level_failed'));
    }
  }

  updateUI() {
    this.updateAccessLevelDisplay();
    this.updatePublicToggle();
  }

  updateAccessLevelDisplay() {
    const accessLevelText = document.querySelector(
      '.general-access-level-text',
    );
    if (accessLevelText) {
      const newText = this.getAccessLevelText();
      accessLevelText.textContent = newText;
    }
  }

  updatePublicToggle() {
    const publicSwitch = document.querySelector('.public-switch input');
    if (!publicSwitch) return;

    if (publicSwitch.checked !== this.isPublic) {
      publicSwitch.checked = this.isPublic;

      const switchSlider = publicSwitch.parentElement.querySelector('div');
      const sliderButton = switchSlider?.querySelector('div');

      if (switchSlider && sliderButton) {
        if (this.isPublic) {
          switchSlider.classList.add('bg-[var(--o-hive-primary-100)]');
          switchSlider.classList.remove('bg-gray-300');
          sliderButton.classList.add('translate-x-6');
        } else {
          switchSlider.classList.remove('bg-[var(--o-hive-primary-100)]');
          switchSlider.classList.add('bg-gray-300');
          sliderButton.classList.remove('translate-x-6');
        }
      }
    }
  }

  async toggleCommentsEnabled(enabled) {
    try {
      const response = await changeCommentsEnabled(
        this.resourceSharingId,
        enabled ? 1 : 0,
      );

      if (response.status.toLowerCase() === 'success') {
        this.commentsEnabled = enabled ? 1 : 0;
        toast.success(
          i18next.t('menubar.share.toast.comments_toggle_success', {
            status: enabled ? 'enabled' : 'disabled',
          }),
        );

        this.onStateChange({
          commentsEnabled: this.commentsEnabled,
          isPublic: this.isPublic,
          cadfileAccessLevel: {
            level: this.currentAccessLevel,
          },
          accessLevelText: this.getAccessLevelText(),
        });
      } else {
        throw new Error('Failed to change comment settings');
      }
    } catch (error) {
      console.error('Error toggling comments:', error);
      toast.error(i18next.t('menubar.share.toast.toggle_comments_failed'));
    }
  }

  async togglePublicPrivate(isPublic) {
    if (isPublic === this.isPublic) return false;

    if (isPublic) {
      const yesNoPrompt = new YesNoPromptModal(
        i18next.t('menubar.share.enable_public_access'),
        i18next.t(
          `menubar.share.enable_public_access_description_${this.isProject ? 'project' : 'task'}`,
        ),
      );
      const confirmed = await yesNoPrompt.show();
      if (!confirmed) {
        return false;
      }
    }
    try {
      const newAccessLevel = isPublic
        ? this.currentAccessLevel !== 'Restricted'
          ? this.currentAccessLevel
          : 'View'
        : 'Restricted';

      await this.changeAccessLevel(newAccessLevel, true);
      return true;
    } catch (error) {
      console.error('Error toggling public access:', error);
      return false;
    }
  }

  getAccessLevelText() {
    if (!this.isPublic) {
      return i18next.t('menubar.share.option_restricted');
    }

    switch (this.currentAccessLevel) {
      case 'Edit':
        return i18next.t('role.admin');
      case 'Restricted':
        return i18next.t('menubar.share.option_restricted');
      case 'View':
        return i18next.t('role.member');
      case 'Manager':
        return i18next.t('menubar.share.option_manager');
      default:
        return i18next.t('menubar.share.option_restricted');
    }
  }
}
